import React, { ReactNode, useEffect, useRef } from 'react'
import { fr, enUS } from 'date-fns/locale'
import { blue } from '@mui/material/colors'
import { DateRangePicker } from 'react-date-range'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import DateRangeIcon from '@mui/icons-material/DateRange'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { camelCase } from 'lodash'
import { useTheme } from '@mui/material/styles'
import snakeCase from 'lodash/snakeCase'

import { locales } from 'lib/utility/LocaleHelper'
import { MONTH_FIRST_DATE_FORMAT, isMobileView } from 'lib/GlobalConstants'
import { DATE_PICKER_MENU_OPTIONS } from 'lib/participants/Constants'
import { AdminUITheme } from 'types/AdminUiThemeMui'
import { hasClickedOutside } from 'components/Shared/DateRange/Helper'
import { useAppSelector } from 'redux/Hook'

interface DateRangeProps {
  siteCreatedAt?: string
  adminLocale?: string
  dispatch?: any
  toggleDateRange?: (selectCustomPeriod: boolean) => void
  data: {
    isDateRangePickerOpen: boolean
  }
  label: string
  onRangeFocusChange?: (arg0: any) => void
  textFieldOnChange: (arg0: any) => void
  calculatedRange?: string
  selectedRangeAsProp?: any
  setFocusedRangeAsProp?: (focusedRange: any) => void
  handleDateRangeChangeAsProp?: (arg0: any) => void
  focusedRangeAsProp?: any
  defaultOpenAsProp?: boolean
  closeDateRangePicker?: () => void
}

const renderSelectOptions = (): ReactNode => {
  const { t } = useTranslation()
  return DATE_PICKER_MENU_OPTIONS.map((status, index) => {
    const camelCased = camelCase(status)

    return (
      <MenuItem key={index} value={status} sx={{ width: '100%' }}>
        <Stack direction="row" spacing={1}>
          <Typography variant="body1">
            {t(`filters.genericDateRange.${camelCased}`)}
          </Typography>
        </Stack>
      </MenuItem>
    )
  })
}

const DateRange = (props: DateRangeProps) => {
  const {
    dispatch,
    toggleDateRange,
    data,
    label,
    textFieldOnChange,
    calculatedRange,
    selectedRangeAsProp,
    setFocusedRangeAsProp,
    handleDateRangeChangeAsProp,
    focusedRangeAsProp,
    defaultOpenAsProp = true,
    closeDateRangePicker,
  }: DateRangeProps = props

  const { t } = useTranslation()
  const { isDateRangePickerOpen } = data
  const ref = useRef<any>()

  const siteCreatedAt = useAppSelector((state) => state.site.site).createdAt
  const adminLocale = useAppSelector((state) => state.user.user).adminLocale

  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      // If the picker is open and the clicked target is not within the picker, then close the picker
      hasClickedOutside(
        e,
        isDateRangePickerOpen,
        ref,
        toggleDateRange,
        dispatch,
        closeDateRangePicker,
      )
    }

    document.addEventListener('mousedown', checkIfClickedOutside)

    return () => {
      // Cleanup the event listener
      document.removeEventListener('mousedown', checkIfClickedOutside)
    }
  }, [isDateRangePickerOpen])

  const theme: AdminUITheme = useTheme()
  return (
    <Stack style={theme.GenericTheme.filterWrapperWidth}>
      <FormControl
        variant="standard"
        fullWidth
        data-cy={`${label.toLowerCase()}_filter_component`}
      >
        <TextField
          select
          color="primary"
          label={t(`filters.${label}.label`)}
          value={calculatedRange}
          onChange={(e) => textFieldOnChange(snakeCase(e.target.value))}
          SelectProps={{
            MenuProps: {
              'data-testid': 'date-range-menu',
              classes: { root: 'date-range-menu' },
            },
            defaultOpen: defaultOpenAsProp,
          }}
          InputProps={{
            style: { width: isMobileView ? '100%' : '15.625rem' },
            startAdornment: (
              <InputAdornment position="start">
                <DateRangeIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        >
          {renderSelectOptions()}
          <MenuItem
            key={calculatedRange}
            value={calculatedRange}
            id={calculatedRange}
            sx={{ display: 'none' }}
            selected={true}
          >
            {calculatedRange}
          </MenuItem>
        </TextField>
        <Box className="date-range-box" ref={ref} data-testid="date-range-box">
          {isDateRangePickerOpen && (
            <DateRangePicker
              minDate={new Date(siteCreatedAt)}
              locale={adminLocale === locales.french ? fr : enUS}
              onChange={handleDateRangeChangeAsProp}
              editableDateInputs={false}
              months={2}
              dateDisplayFormat={MONTH_FIRST_DATE_FORMAT}
              ranges={[selectedRangeAsProp]}
              maxDate={new Date()}
              showDateDisplay={false}
              staticRanges={[]}
              inputRanges={[]}
              focusedRange={focusedRangeAsProp}
              onRangeFocusChange={setFocusedRangeAsProp}
              direction="horizontal"
              rangeColors={[blue[700]]}
              color={blue[700]}
            />
          )}
        </Box>
      </FormControl>
    </Stack>
  )
}

export default DateRange
